<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { eventBus, getStructureImageUrl, RESOLVER_URL, prepareLinks } from '@/main'
import PubchemInfoCard from '@/components/pubchem/PubchemInfoCard.vue'
import { getChemicalDomId, getChemicalTitleExceptCasrn, getChemicalCasrnWithFallBack } from '@/utils/chemical'
import { defaultVTooltipProps } from '@/utils/defaultProps'
import ChemPropsWithCopyCardRenderer from '@/components/shared-table/components/renderers/ChemPropsWithCopyCardRenderer.vue'

const props = defineProps({
  chemical: { type: Object, required: true },
  requestChemical: { type: Object, default: null },
  showImages: { type: Boolean, default: false },
  showCheckbox: { type: Boolean, default: false },
  showSimilaritySortingIcon: { type: Boolean, default: false },
  maxWidth: { type: Number, default: 150 },
  imgMaxHeight: { type: Number, default: 50 },
})

const emit = defineEmits(['check-click', 'similarity-click'])

const links = ref(null)
const hasPubchemInfo = ref(false)

const instance = getCurrentInstance()
const $axios = instance && instance.proxy ? instance.proxy.$axios : null

const linksWithoutSpectra = computed(() => {
  return links.value ? prepareLinks(links.value) : []
})

const domId = computed(() => {
  return getChemicalDomId(props.chemical)
})

const onOpenLarge = (smiles) => {
  eventBus.$emit('SHOW_LARGE_IMG', smiles)
}

const getLinks = (chemical) => {
  if (!links.value && chemical && chemical.sid && $axios) {
    $axios
      .get(`${RESOLVER_URL}/links?query=${chemical.sid}`)
      .then((response) => (links.value = response.data[0] ? response.data[0].links : null))
      .catch(() => (links.value = null))
  }
}

const getLinksLetters = (links) => {
  return Array.from(new Set(links.map((l) => ('ChemExpo' === l.name ? 'E' : l.name.substr(0, 1))))).join('')
}

const updateHasPubchemInfo = (hasInfo) => {
  hasPubchemInfo.value = hasInfo
}

const shouldShowFirstRow = computed(
  () => !!props.showCheckbox || !!linksWithoutSpectra.value?.length || !!hasPubchemInfo.value,
)

onMounted(() => {
  getLinks(props.chemical)
})
</script>

<template>
  <div
    class="d-flex flex-column p-1"
    :style="{ maxWidth: maxWidth ? maxWidth + 'px' : '150px' }"
  >
    <div
      v-show="shouldShowFirstRow"
      class="d-flex flex-row justify-content-between align-items-center"
      style="gap: 0.5rem"
    >
      <input
        v-if="showCheckbox"
        :checked="chemical.checked"
        type="checkbox"
        @input="emit('check-click', chemical)"
        :aria-label="`Select ${getChemicalTitleExceptCasrn(chemical)}`"
      />

      <v-spacer />

      <div
        class="d-flex flex-row align-items-center"
        style="gap: 0.25rem"
      >
        <div
          v-if="linksWithoutSpectra?.length"
          style="font-size: 0.65rem; opacity: 0.5"
        >
          {{ getLinksLetters(linksWithoutSpectra) }}
        </div>

        <v-menu
          :close-on-content-click="false"
          :open-delay="defaultVTooltipProps.openDelay"
          open-on-hover
          offset-x
          eager
          role="none"
        >
          <template #activator="{ on, attrs }">
            <v-icon
              v-show="hasPubchemInfo"
              small
              style="cursor: help"
              v-bind="attrs"
              v-on="on"
            >
              mdi-information-outline
            </v-icon>
          </template>

          <PubchemInfoCard
            section="Record Description"
            :chemical="chemical"
            @has-result="updateHasPubchemInfo"
          />
        </v-menu>
      </div>
    </div>

    <v-menu
      :close-on-content-click="false"
      open-on-hover
      v-bind="defaultVTooltipProps"
      offset-x
      nudge-top="20"
      role="none"
    >
      <template #activator="{ on }">
        <div
          class="d-flex flex-column"
          :class="{ 'mt-1': shouldShowFirstRow }"
          style="cursor: help; gap: 0.25rem"
          v-on="on"
        >
          <v-img
            v-if="showImages"
            :src="getStructureImageUrl(chemical)"
            :alt="'Image of chemical structure ' + getChemicalTitleExceptCasrn(chemical)"
            :max-width="maxWidth"
            :max-height="imgMaxHeight"
            @click="onOpenLarge(chemical.smiles)"
            contain
          />

          <div
            :style="{ lineHeight: showImages ? '1.3' : '', maxWidth: '100%' }"
            :class="showImages ? 'text-center' : 'text-start'"
          >
            <div class="text-truncate">
              {{ getChemicalTitleExceptCasrn(chemical) ?? 'N/A' }}
            </div>

            <div class="text-truncate small text-muted">{{ getChemicalCasrnWithFallBack(chemical) }}</div>
          </div>

          <!-- <div
          v-if="showSmiles"
          class="pt-1 px-1 text-start"
          v-bind:class="{ 'smiles-truncate':  }"
        >
          {{ chemical.smiles || 'No SMILES' }}
        </div> -->
        </div>
      </template>

      <ChemPropsWithCopyCardRenderer :chemical="chemical" />
    </v-menu>

    <div
      v-if="requestChemical && requestChemical.properties"
      class="d-flex flex-row align-items-center mt-1"
      style="gap: 0.5rem"
    >
      <button
        v-if="'similarity' in requestChemical.properties"
        type="button"
        @click="emit('similarity-click')"
        :aria-label="`Sort by similarity to ${getChemicalTitleExceptCasrn(chemical)}`"
      >
        <v-chip
          :id="`chem-cell-similarity-number-${domId}`"
          label
        >
          {{ requestChemical.properties.similarity | number('0.00') }}
        </v-chip>

        <v-tooltip
          v-if="showSimilaritySortingIcon"
          :activator="`#chem-cell-similarity-number-${domId}`"
          v-bind="defaultVTooltipProps"
          right
          color="white"
        >
          <span>
            <v-icon color="black">mdi-sort</v-icon>
          </span>
        </v-tooltip>
      </button>

      <div
        v-if="'ctsChemical' in requestChemical.properties"
        :class="requestChemical.properties.ctsChemical.likelihood === 'LIKELY' ? 'text-success' : 'text-muted'"
      >
        {{ requestChemical.properties.ctsChemical.likelihood }}
      </div>
    </div>
  </div>
</template>
